import React, { useState, useEffect } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { api } from './apiClient';

export function formatCurrency(val) {
    if (val == null) return '—';
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${Number(val).toFixed(2)}`;
}

/**
 * The product detail slide-out (legacy Products.jsx right panel).
 *
 * Props:
 *   asin, storeId  — the selected listing (storeId is the row's store_id)
 *   product        — the row, with sibling SKUs' inventory already summed by Products
 *   isMobile       — layout switch, from Products' breakpoint hook
 *   onClose()      — clear the selection and go back to /amazon-pricing-hub
 *   onSaved(min, max) — Repricer min/max saved; Products patches its rows and the Broken count
 *   onRefreshed()  — "Refresh All" finished; Products reloads the grid
 */
export default function ProductDetail({ asin, storeId, product, isMobile, onClose, onSaved, onRefreshed }) {
    const [salesData, setSalesData] = useState(null);
    const [chartDays, setChartDays] = useState(30);
    const [chartMetric, setChartMetric] = useState('revenue');
    const [competitors, setCompetitors] = useState([]);
    const [checkingStock, setCheckingStock] = useState(false);
    const [stockStatus, setStockStatus] = useState('');
    const [editMin, setEditMin] = useState('');
    const [editMax, setEditMax] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [refreshStatus, setRefreshStatus] = useState('');

    useEffect(() => {
        api.sales(asin, chartDays, storeId).then(setSalesData).catch(() => {});
        api.competitors(asin).then(setCompetitors).catch(() => setCompetitors([]));
        setSaveMsg('');
    }, [asin, chartDays, storeId]);

    // Keep the min/max inputs in sync with the row.
    useEffect(() => {
        setEditMin(product.price_min != null ? Number(product.price_min).toFixed(2) : '');
        setEditMax(product.price_max != null ? Number(product.price_max).toFixed(2) : '');
    }, [product.price_min, product.price_max]);

    return (
        <>
        <div onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 299 }} />
        <div style={isMobile ? {
            position: 'fixed', top: '3vh', left: '3vw', right: '3vw', bottom: '3vh', zIndex: 300,
            background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', overflowY: 'auto',
            padding: 16,
        } : {
            position: 'fixed', top: 0, right: 0, width: 650, height: '100vh', zIndex: 300,
            background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', overflowY: 'auto',
            padding: 24, transition: 'transform 0.2s ease',
        }}>
            {/* Close */}
            <button onClick={onClose}
                style={{ position: 'absolute', top: isMobile ? 8 : 12, right: isMobile ? 8 : 16, background: isMobile ? '#f0f0f0' : 'none', border: 'none', fontSize: isMobile ? 26 : 22, cursor: 'pointer', color: '#666', borderRadius: isMobile ? '50%' : 0, width: isMobile ? 36 : 'auto', height: isMobile ? 36 : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >✕</button>

            {/* Image + Title */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
                <img
                    src={`https://m.media-amazon.com/images/P/${product.asin}.01._SCLZZZZZZZ_SX200_.jpg`}
                    alt="" style={{ width: 70, height: 70, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 2, paddingRight: 24 }}>{product.title}</h2>
                    <div style={{ fontSize: 12, color: product.status === 'Active' ? '#008060' : '#e04e39', marginBottom: 4 }}>
                        {`Amazon status: ${product.status === 'Active' ? 'buyable' : 'not buyable'}`}
                        {product.status !== 'Active' && product.listing_reason ? ` · ${product.listing_reason}` : ''}
                        {product.listing_checked_at ? ` · checked ${new Date(product.listing_checked_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ' · not checked live yet'}
                    </div>
                    {product.status !== 'Active' && Array.isArray(product.listing_issues) && product.listing_issues.length > 0 && (
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
                            {String(product.listing_issues[0].message || '').slice(0, 300)}
                        </div>
                    )}
                    {product.store_display_name && (
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                            Store: {product.store_display_name}
                        </div>
                    )}
                    <div style={{ color: '#666', fontSize: 12 }}>
                        {product.sku} · {product.asin}
                        <a href={`https://www.amazon.com/dp/${product.asin}`} target="_blank" rel="noopener noreferrer"
                            style={{ marginLeft: 8, color: '#5c6ac4', textDecoration: 'none', fontWeight: 600, userSelect: 'none' }}
                        >View on Amazon ↗</a>
                        <a href={`https://dashboard-3.repricer.com/repricer?fsf_query=${product.asin}`} target="_blank" rel="noopener noreferrer"
                            style={{ marginLeft: 8, color: '#e04e39', textDecoration: 'none', fontWeight: 600, userSelect: 'none' }}
                        >View on Repricer ↗</a>
                    </div>
                </div>
            </div>

            {/* Grouped sections — full width */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                        {/* Pricing */}
                        <InfoGroup title="Pricing">
                            <InfoRow label="My Price" value={product.my_price != null ? `$${Number(product.my_price).toFixed(2)}` : '—'} />
                            <InfoRow label="Sale Price" value={product.sale_price != null ? `$${Number(product.sale_price).toFixed(2)}` : '—'} color="#e04e39" />
                            <InfoRow label="Buy Box" value={product.buy_box_price != null ? `$${Number(product.buy_box_price).toFixed(2)}` : '—'}
                                color={product.buy_box_winner === 'You' ? '#008060' : '#e04e39'} />
                            <InfoRow label="Repr. Min" value={product.price_min != null ? `$${Number(product.price_min).toFixed(2)}` : '—'} />
                            <InfoRow label="Repr. Max" value={product.price_max != null ? `$${Number(product.price_max).toFixed(2)}` : '—'} />
                        </InfoGroup>

                        {/* Inventory */}
                        <InfoGroup title="Inventory">
                            <InfoRow label="Available" value={product.qty_available ?? '—'} />
                            <InfoRow label="Inbound" value={product.qty_inbound || '—'} />
                            <InfoRow label="FBA" value={product.fba ? 'Yes' : 'No'} />
                            <InfoRow label="Rank" value={product.sales_rank ? product.sales_rank.toLocaleString() : '—'} />
                            <InfoRow label="Sellers" value={product.competitor_count ?? '—'} />
                            {salesData && (() => {
                                const qty = product.qty_available || 0;
                                const d7 = qty && salesData.stats.units_7d > 0 ? Math.round(qty / (salesData.stats.units_7d / 7)) : '∞';
                                const d30 = qty && salesData.stats.units_30d > 0 ? Math.round(qty / (salesData.stats.units_30d / 30)) : '∞';
                                const d90 = qty && salesData.stats.units_90d > 0 ? Math.round(qty / (salesData.stats.units_90d / 90)) : '∞';
                                return (<>
                                    <InfoRow label="DOI 7d" value={d7} />
                                    <InfoRow label="DOI 30d" value={d30} />
                                    <InfoRow label="DOI 90d" value={d90} />
                                </>);
                            })()}
                        </InfoGroup>

                        {/* Fees */}
                        <InfoGroup title="Fees">
                            <InfoRow label="Referral" value={product.referral_fee != null ? `$${Number(product.referral_fee).toFixed(2)}` : '—'} />
                            <InfoRow label="FBA Fee" value={product.fba_fee != null ? `$${Number(product.fba_fee).toFixed(2)}` : '—'} />
                            <InfoRow label="Total" value={product.estimated_fees != null ? `$${Number(product.estimated_fees).toFixed(2)}` : '—'} bold />
                            <InfoRow label="Est. Storage" value={product.storage_fee_next_month != null ? `$${Number(product.storage_fee_next_month).toFixed(2)}` : '—'} />
                        </InfoGroup>

            </div>

            {/* Sales — 7d / 30d / 90d side-by-side */}
            <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Sales</div>
                {salesData ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                        <SalesPeriodCard label="7 days" revenue={salesData.stats.revenue_7d} units={salesData.stats.units_7d} />
                        <SalesPeriodCard label="30 days" revenue={salesData.stats.revenue_30d} units={salesData.stats.units_30d} />
                        <SalesPeriodCard label="90 days" revenue={salesData.stats.revenue_90d} units={salesData.stats.units_90d} />
                    </div>
                ) : (
                    <div style={{ fontSize: 12, color: '#888', background: '#f9fafb', borderRadius: 6, border: '1px solid #eee', padding: '6px 10px' }}>Loading...</div>
                )}
            </div>

            {salesData && (<>
            {/* Chart toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                {[7, 30, 90].map(d => (
                    <button key={d} onClick={() => setChartDays(d)}
                        style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', border: 'none',
                            background: chartDays === d ? '#5c6ac4' : '#f0f0f0', color: chartDays === d ? 'white' : '#666', fontWeight: chartDays === d ? 600 : 400 }}
                    >{d}d</button>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    {[{ key: 'revenue', label: 'Revenue' }, { key: 'units', label: 'Sales' }].map(m => (
                        <button key={m.key} onClick={() => setChartMetric(m.key)}
                            style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', border: 'none',
                                background: chartMetric === m.key ? '#5c6ac4' : '#f0f0f0', color: chartMetric === m.key ? 'white' : '#666', fontWeight: chartMetric === m.key ? 600 : 400 }}
                        >{m.label}</button>
                    ))}
                </div>
            </div>

            {/* Chart */}
            <div style={{ height: 200, marginBottom: 12 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={salesData.daily}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => chartMetric === 'revenue' ? `$${v}` : v} allowDecimals={false} />
                        <Tooltip formatter={(val) => chartMetric === 'revenue'
                                ? [`$${Number(val).toFixed(2)}`, 'Revenue']
                                : [Number(val), 'Units']}
                            labelFormatter={d => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
                        <Area type="monotone" dataKey={chartMetric} stroke="#5c6ac4" fill="#5c6ac4" fillOpacity={0.15} strokeWidth={2} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            </>)}

            {/* Refresh All + Repricing Rule */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid #e0e0e0', paddingTop: 10, marginBottom: 10 }}>
                <button
                    disabled={refreshing}
                    onClick={async () => {
                        setRefreshing(true);
                        setRefreshStatus('Refreshing...');
                        let statusMsg = '';
                        let result = null;
                        let err = null;
                        for (let attempt = 1; attempt <= 2; attempt++) {
                            try {
                                result = await api.refreshProduct(asin, product.store_id);
                                err = null;
                                break;
                            } catch (e) {
                                err = e;
                                result = null;
                                if (e.status >= 400 && e.status < 500) break; // don't retry real 4xx
                            }
                            if (attempt === 1) {
                                setRefreshStatus('Retrying...');
                                await new Promise(r => setTimeout(r, 1500));
                            }
                        }
                        if (err) {
                            statusMsg = err.status ? `Error ${err.status}` : `Error: ${err.message || 'network'}`;
                        } else {
                            result = result || {};
                            if (result.error) {
                                statusMsg = `Error: ${String(result.error).slice(0, 40)}`;
                            } else {
                                try {
                                    setCompetitors(await api.competitors(asin));
                                } catch (_) { /* competitor refetch is best-effort */ }
                                // Refetch the products table so the row's My Price /
                                // Buy Box / Lowest reflect the freshly-written values.
                                onRefreshed();
                                statusMsg = `Done — ${result.offers ?? ''} offers`;
                            }
                        }
                        setRefreshStatus(statusMsg);
                        setTimeout(() => { setRefreshing(false); setRefreshStatus(''); }, 2500);
                    }}
                    style={{
                        padding: '4px 10px', borderRadius: 4, border: 'none',
                        background: refreshing ? '#999' : '#5c6ac4', color: 'white',
                        fontSize: 11, fontWeight: 600, cursor: refreshing ? 'default' : 'pointer',
                    }}
                >{refreshing ? refreshStatus || '...' : '↻ Refresh All'}</button>
                {product.repricer_name && <span style={{ fontSize: 11, color: '#999' }}>Rule: {product.repricer_name}</span>}
            </div>

            {/* Repricing */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #e0e0e0', paddingTop: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>Repricing:</span>
                <label style={{ fontSize: 11, color: '#666' }}>Min $</label>
                <input value={editMin} onChange={e => setEditMin(e.target.value)}
                    style={{ width: 70, padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc', fontSize: 12 }}
                    type="number" step="0.01" min="0" />
                <label style={{ fontSize: 11, color: '#666' }}>Max $</label>
                <input value={editMax} onChange={e => setEditMax(e.target.value)}
                    style={{ width: 70, padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc', fontSize: 12 }}
                    type="number" step="0.01" min="0" />
                <button disabled={saving || !editMin || !editMax}
                    onClick={async () => {
                        setSaving(true); setSaveMsg('');
                        try {
                            const res = await api.repricerUpdate({ sku: product.sku, store_id: product.store_id, price_min: parseFloat(editMin), price_max: parseFloat(editMax) });
                            setSaveMsg(res.repricer_pushed === false ? 'Saved locally (Repricer pushes are off)' : 'Saved');
                            onSaved(parseFloat(editMin), parseFloat(editMax));
                        } catch (e) { setSaveMsg('Error: ' + (e.message || 'Failed')); }
                        setSaving(false); setTimeout(() => setSaveMsg(''), 3000);
                    }}
                    style={{ padding: '3px 10px', borderRadius: 4, border: 'none', background: saving ? '#999' : '#5c6ac4', color: 'white', fontSize: 11, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}
                >{saving ? '...' : 'Save'}</button>
                {saveMsg && <span style={{ fontSize: 11, color: saveMsg.startsWith('Error') ? '#e04e39' : '#008060', fontWeight: 600 }}>{saveMsg}</span>}
                {product.repricer_name && <div style={{ fontSize: 10, color: '#999', width: '100%' }}>Rule: {product.repricer_name}</div>}
            </div>

            {/* Competitor Offers */}
            <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
                        Competitors {competitors.length > 0 && `(${competitors.length})`}
                    </h3>
                    <button onClick={async () => {
                            setCheckingStock(true); setCompetitors([]); setStockStatus('Checking stock… (if Keepa is out of tokens this waits up to 90 s)');
                            let statusLingerMs = 2000;
                            try {
                                const result = await api.checkStock(asin, product.store_id)
                                    .catch((e) => { if (e.status) return {}; throw e; }); // HTTP error: carry on as legacy did
                                const list = await api.competitors(asin).catch(() => null);
                                if (list) setCompetitors(list);
                                if (result.error) {
                                    // Surface degraded / rate-limit conditions so the user knows
                                    // to retry instead of assuming the "—" quantities are real.
                                    setStockStatus(`⚠ ${result.error}`);
                                    statusLingerMs = 8000;
                                } else {
                                    setStockStatus(`${result.offers || 0} offers`);
                                }
                            } catch (e) { setStockStatus('Error'); }
                            await new Promise(r => setTimeout(r, statusLingerMs));
                            setCheckingStock(false); setStockStatus('');
                        }}
                        disabled={checkingStock}
                        style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #5c6ac4', background: checkingStock ? '#f5f5f5' : 'white', color: '#5c6ac4', fontSize: 11, cursor: checkingStock ? 'default' : 'pointer', fontWeight: 600 }}
                    >{checkingStock ? '...' : 'Check Stock'}</button>
                    {stockStatus && <span style={{ fontSize: 11, color: stockStatus.startsWith('⚠') ? '#e04e39' : '#5c6ac4', fontWeight: stockStatus.startsWith('⚠') ? 600 : 400 }}>{stockStatus}</span>}
                </div>
                {competitors.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap' }}>
                            <thead>
                                <tr style={{ background: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                                    <th style={compTh}>Seller</th>
                                    <th style={{ ...compTh, textAlign: 'right' }}>Price</th>
                                    <th style={{ ...compTh, textAlign: 'center' }}>BB</th>
                                    <th style={{ ...compTh, textAlign: 'right' }}>Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {competitors.map((c, i) => {
                                    const isYou = c.seller_id === 'A2DVTJZVDB8BHY';
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: isYou ? '#fffde7' : c.buybox_winner ? '#e8f5e9' : 'transparent' }}>
                                            <td style={compTd}><strong style={{ color: isYou ? '#5c6ac4' : '#333' }}>{isYou ? '⭐ ' : ''}{c.seller_name || '—'}</strong></td>
                                            <td style={{ ...compTd, textAlign: 'right', fontWeight: 600 }}>{c.offer_price != null ? `$${Number(c.offer_price).toFixed(2)}` : '—'}</td>
                                            <td style={{ ...compTd, textAlign: 'center' }}>{c.buybox_winner ? '🏆' : '—'}</td>
                                            <td style={{ ...compTd, textAlign: 'right' }}>{c.stock_level != null ? c.stock_level : (c.in_stock ? '✓' : '—')}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p style={{ color: '#999', fontSize: 12 }}>No competitor data. Click "Check Stock".</p>
                )}
            </div>
        </div>
        </>
    );
}

function InfoGroup({ title, children }) {
    return (
        <div style={{ background: '#f9fafb', borderRadius: 6, padding: '6px 10px', border: '1px solid #eee' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{title}</div>
            {children}
        </div>
    );
}

function InfoRow({ label, value, color, bold }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '1px 0' }}>
            <span style={{ color: '#888' }}>{label}</span>
            <span style={{ fontWeight: bold ? 700 : 600, color: color || '#333' }}>{value}</span>
        </div>
    );
}

function SalesPeriodCard({ label, revenue, units }) {
    return (
        <div style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 10px', border: '1px solid #eee', textAlign: 'center' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>{formatCurrency(revenue)}</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{units} {units === 1 ? 'unit' : 'units'}</div>
        </div>
    );
}

const compTh = { padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#666' };
const compTd = { padding: '6px 10px' };
